import React, { useState, useEffect } from 'react';
import { PlayCircle, RefreshCw, Sparkles } from 'lucide-react';
import useUnitCode from '../hooks/useUnitCode';
import UnitCard from '../components/unit/UnitCard';
import { MyUnitCodeDto } from '../types/unit';
import { CONTENT_MESSAGES } from '../utils/contentMessages';
import { useNavigate, useLocation } from 'react-router-dom';

interface MyLecturesProps {
  embedded?: boolean;
}

const MyLectures: React.FC<MyLecturesProps> = ({ embedded = false }) => {
  const {
    units,
    isLoading,
    error,
    totalCount,
    totalPages,
    currentPage,
    getMyUnits,
    clearError,
    refreshUnits,
  } = useUnitCode();

  const navigate = useNavigate();
  const location = useLocation();
  const [filteredUnits, setFilteredUnits] = useState<MyUnitCodeDto[]>([]);

  useEffect(() => {
    refreshUnits();
  }, [location.key, refreshUnits]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('refresh')) {
      refreshUnits();
      navigate(embedded ? '/student-portal' : '/my-lectures', { replace: true });
    }
  }, [location.search, refreshUnits, navigate, embedded]);

  useEffect(() => {
    setFilteredUnits(units);
  }, [units]);

  const handlePageChange = (page: number) => {
    getMyUnits(page);
  };

  const handleRefresh = () => {
    clearError();
    refreshUnits();
  };

  const handleUnitClick = (unitId: number) => {
    navigate(`/unit-viewer/${unitId}`);
  };

  const UnitCardShimmer = () => (
    <div className="animate-pulse overflow-hidden rounded-xl border-2 border-teal-100 bg-white">
      <div className="bg-teal-50 p-3">
        <div className="mx-auto mb-2 h-10 w-10 rounded-xl bg-teal-200" />
      </div>
      <div className="p-3">
        <div className="mb-1.5 h-4 rounded bg-slate-200" />
        <div className="mb-3 h-3 rounded bg-slate-100" />
        <div className="h-8 rounded bg-teal-100" />
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="rounded-2xl border-2 border-teal-100 bg-white py-12 text-center">
      <div className="mx-auto mb-4 inline-flex rounded-2xl bg-teal-50 p-4 text-teal-600">
        <PlayCircle className="h-12 w-12" />
      </div>
      <h3 className="mb-3 text-lg font-extrabold text-slate-900">مفيش محاضرات دلوقتي</h3>
      <p className="mx-auto max-w-md text-sm leading-relaxed text-slate-600">
        لسه مدخلتش أي كود محاضرة. ادخل الكود من صفحة الكورسات عشان تتفتحلك الحصة.
      </p>
    </div>
  );

  const ErrorState = () => (
    <div className="rounded-2xl border-2 border-teal-100 bg-white py-12 text-center">
      <h3 className="mb-3 text-lg font-extrabold text-slate-900">حصل مشكلة في تحميل الحصص</h3>
      <p className="mb-4 text-sm text-slate-600">
        {/no enrolled units found/i.test(String(error)) ? 'مفيش حصص مسجّلة دلوقتي' : error}
      </p>
      <button
        type="button"
        onClick={handleRefresh}
        className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
      >
        <RefreshCw className="h-3 w-3" />
        إعادة المحاولة
      </button>
    </div>
  );

  return (
    <div
      className={embedded ? '' : 'min-h-screen bg-slate-50 pt-20'}
      dir="rtl"
    >
      <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${embedded ? 'pb-8' : 'py-8'}`}>
        {!embedded && (
          <div className="mb-8 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-teal-100 bg-teal-50 px-4 py-2">
              <PlayCircle className="h-4 w-4 text-teal-600" />
              <span className="text-sm font-semibold text-teal-700">محاضراتي</span>
            </div>
            <h1 className="mb-4 text-2xl font-extrabold text-slate-900 lg:text-3xl">
              <span className="inline-flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-teal-600 shadow-md">
                  <Sparkles className="h-4 w-4 text-white" />
                </span>
                محاضرات السنتر
              </span>
            </h1>
          </div>
        )}

        {!isLoading && !error && (
          <div className="mb-6 text-center">
            <p className="text-sm text-slate-600">
              {filteredUnits.length > 0 ? (
                <>
                  عرض <span className="font-semibold text-teal-600">{filteredUnits.length}</span> من
                  أصل <span className="font-semibold text-teal-600">{totalCount}</span> وحدة
                </>
              ) : (
                CONTENT_MESSAGES.noUnits
              )}
            </p>
          </div>
        )}

        {error ? (
          <ErrorState />
        ) : isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <UnitCardShimmer key={`shimmer-${index}`} />
            ))}
          </div>
        ) : filteredUnits.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {filteredUnits.map((unit) => (
              <div
                key={unit.unitId}
                onClick={() => handleUnitClick(unit.unitId)}
                className="cursor-pointer"
                role="presentation"
              >
                <UnitCard unit={unit} />
              </div>
            ))}
          </div>
        )}

        {!isLoading && !error && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded-xl border-2 border-teal-100 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-teal-50 disabled:opacity-50"
            >
              السابق
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={`rounded-xl px-3 py-1.5 text-sm font-semibold ${
                    currentPage === page
                      ? 'bg-teal-600 text-white'
                      : 'border-2 border-teal-100 bg-white text-slate-700 hover:bg-teal-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="rounded-xl border-2 border-teal-100 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-teal-50 disabled:opacity-50"
            >
              التالي
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLectures;
